// Georgian typed in Latin letters („gaiyida ukve", „ar tanamshromlobs") → a Georgian-letter SUGGESTION.
//
// Why it exists (2026-09-16): an agent's call comments were written in Latin letters, and every
// reader of those comments — Georgian search, the reading lane — matches Georgian letters only.
//
// ⚠️ A SUGGESTION, NEVER AN AUTOMATIC REWRITE. The chat convention is ambiguous: `k` is usually კ but
// some write ქ, `t` is თ while `T` is ტ, `p` is პ while `f`/`ph` is ფ. The agent sees the result and
// presses to take it; what they typed is never changed behind them.
//
// Returns null when the text is not worth offering: empty, already has Georgian letters, has
// Cyrillic, or reads as English.

// Longest first: a digraph must win over its first letter.
const MULTI = [
  ['tsh', 'ჭ'], ['ch\'', 'ჭ'], ['ts\'', 'წ'],
  ['sh', 'შ'], ['ch', 'ჩ'], ['zh', 'ჟ'], ['kh', 'ხ'], ['gh', 'ღ'], ['dz', 'ძ'], ['ts', 'ც'],
  ['ph', 'ფ'], ['k\'', 'კ'], ['p\'', 'პ'], ['t\'', 'ტ'],
]
// Case matters where the convention uses it: T ტ, S შ, C ჩ, W ჭ, Z ჟ, R ღ, J ჯ.
const CASED = { T: 'ტ', S: 'შ', C: 'ჩ', W: 'ჭ', Z: 'ჟ', R: 'ღ' }
const SINGLE = {
  a: 'ა', b: 'ბ', g: 'გ', d: 'დ', e: 'ე', v: 'ვ', z: 'ზ', t: 'თ', i: 'ი', k: 'კ', l: 'ლ', m: 'მ',
  n: 'ნ', o: 'ო', p: 'პ', r: 'რ', s: 'ს', u: 'უ', f: 'ფ', q: 'ქ', y: 'ყ', w: 'წ', c: 'ც', j: 'ჯ',
  h: 'ჰ', x: 'ხ',
}

// Short, very common English words. One of them in a message means it is probably English, and a
// Georgian rendering of English is noise, not help.
const ENGLISH = new Set(('the is are was and or not no yes of to in on for with this that owner flat ' +
  'sold rented price call called said has have will would but it he she they we you i a an by from ' +
  'at as be do does did can cannot agent number phone again later').split(' '))

export function latinToGeorgian(text) {
  let out = ''
  for (let i = 0; i < text.length;) {
    const rest = text.slice(i)
    const low = rest.toLowerCase()
    const m = MULTI.find(([lat]) => low.startsWith(lat))
    if (m) { out += m[1]; i += m[0].length; continue }
    const ch = text[i]
    if (CASED[ch]) { out += CASED[ch]; i++; continue }
    const g = SINGLE[ch.toLowerCase()]
    out += g || ch
    i++
  }
  return out
}

export function latinGeorgianSuggestion(text) {
  const s = String(text || '').trim()
  if (!s) return null
  if (/[Ⴀ-ჿᲐ-Ჿ]/.test(s)) return null   // already Georgian (incl. Mtavruli)
  if (/[Ѐ-ӿ]/.test(s)) return null                // Cyrillic — a different problem
  const words = s.toLowerCase().match(/[a-z']+/g) || []
  const letters = words.join('').length
  if (letters < 4) return null
  if (words.some(w => ENGLISH.has(w))) return null
  return latinToGeorgian(s)
}
